import {
  Box as BoxIcon,
  Cog as GearSixIcon,
  Flame as FireIcon,
  Leaf as LeafIcon,
  MapPin as MapPinAreaIcon,
  Snowflake as SnowflakeIcon,
  Thermometer as ThermometerIcon,
  Warehouse as GarageIcon
} from 'lucide-react'

/**
 * `AreaKind` enum from `tools/ontology-generator/src/nodespec/spatial.json`.
 * Kept as a plain union here (rather than importing the generated TS) so
 * this helper has no cross-package coupling to the ontology core types.
 */
export type AreaKindKey =
  | 'production_zone'
  | 'cooler'
  | 'freezer'
  | 'utility_room'
  | 'machinery_room'
  | 'mezzanine'
  | 'outdoor'
  | 'other'

/** `AreaTemperatureClass` enum from `spatial.json`. */
export type AreaTemperatureClassKey = 'ambient' | 'refrigerated' | 'frozen' | 'heated'

const AREA_KIND_LABELS: Record<AreaKindKey, string> = {
  production_zone: 'Production zone',
  cooler: 'Cooler',
  freezer: 'Freezer',
  utility_room: 'Utility room',
  machinery_room: 'Machinery room',
  mezzanine: 'Mezzanine',
  outdoor: 'Outdoor',
  other: 'Other'
}

const TEMPERATURE_CLASS_LABELS: Record<AreaTemperatureClassKey, string> = {
  ambient: 'Ambient',
  refrigerated: 'Refrigerated',
  frozen: 'Frozen',
  heated: 'Heated'
}

/** Coerce a free-form `area_kind` string to its display label. */
export function areaKindLabel(kind: string | undefined | null): string | null {
  if (!kind) return null
  if (kind in AREA_KIND_LABELS) return AREA_KIND_LABELS[kind as AreaKindKey]
  return kind
}

/** Coerce a free-form `temperature_class` string to its display label. */
export function temperatureClassLabel(value: string | undefined | null): string | null {
  if (!value) return null
  if (value in TEMPERATURE_CLASS_LABELS) {
    return TEMPERATURE_CLASS_LABELS[value as AreaTemperatureClassKey]
  }
  return value
}

/**
 * Pick a Phosphor icon for an `area_kind` value. Falls back to a
 * generic map-pin glyph when the kind is unknown or missing so cards
 * always render an icon slot.
 */
export function areaKindIcon(kind: string | undefined | null) {
  switch (kind) {
    case 'cooler':
    case 'freezer':
      return SnowflakeIcon
    case 'production_zone':
      return GearSixIcon
    case 'utility_room':
      return BoxIcon
    case 'machinery_room':
      return GarageIcon
    case 'mezzanine':
      return BoxIcon
    case 'outdoor':
      return LeafIcon
    default:
      return MapPinAreaIcon
  }
}

/** Pick a Phosphor icon for a `temperature_class` value. */
export function temperatureClassIcon(value: string | undefined | null) {
  switch (value) {
    case 'refrigerated':
    case 'frozen':
      return SnowflakeIcon
    case 'heated':
      return FireIcon
    default:
      return ThermometerIcon
  }
}
